<template>
    <div class="iph-root">

        <!-- ══ WELCOME STRIP (same structure & spacing as d-welcome) ══ -->
        <div class="iph-welcome anim-up" style="--d:0ms">
            <!-- Decorative orbs -->
            <div class="iph-orb iph-orb--1"></div>
            <div class="iph-orb iph-orb--2"></div>
            <!-- Top shimmer accent line -->
            <div class="iph-hero-line" :style="`background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.5),rgba(34,211,238,0.28),transparent)`"></div>

            <div class="iph-wl-body">
                <!-- Left: Badge + Title -->
                <div class="iph-wl-left">
                    <div class="iph-wl-av" :style="`background:linear-gradient(135deg,${themeColors.primaryDark},${themeColors.primary});box-shadow:0 0 14px rgba(${themeColors.primaryRgb},0.4);`">
                        <svg width="18" height="18" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" viewBox="0 0 24 24">
                            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
                        </svg>
                    </div>
                    <div>
                        <p class="iph-wl-kicker" :style="`color:rgba(${themeColors.primaryRgb},0.7);`">PORTFOLIO ANALYTICS</p>
                        <p class="iph-wl-title">Premium Plan &amp; Staking Pool</p>
                    </div>
                </div>

                <!-- Right: stat chips (same pattern as dw-chips) -->
                <div class="iph-wl-chips">
                    <div class="iph-wl-chip">
                        <span class="iph-wl-chip-l">Best APY</span>
                        <span class="iph-wl-chip-v" :style="`color:${themeColors.primary}`">{{ stats.bestApy }}%</span>
                    </div>
                    <div class="iph-wl-chip">
                        <span class="iph-wl-chip-l">Plans</span>
                        <span class="iph-wl-chip-v">{{ stats.activePlans }}</span>
                    </div>
                    <div class="iph-wl-chip">
                        <span class="iph-wl-chip-l">Investors</span>
                        <span class="iph-wl-chip-v" style="color:#10b981">{{ stats.activeUsers.toLocaleString() }}+</span>
                    </div>
                    <!-- Balance chip — same as dw-bal -->
                    <div class="iph-wl-bal" :style="`background:rgba(${themeColors.primaryRgb},0.10);border-color:rgba(${themeColors.primaryRgb},0.22);`">
                        <div class="iph-wl-bal-top">
                            <span class="iph-wl-chip-l">Total Invested</span>
                            <span class="iph-wl-live" :style="`background:rgba(${themeColors.primaryRgb},0.15);color:${themeColors.primary};`">
                                <i class="iph-wl-dot" :style="`background:${themeColors.primary};`"></i>LIVE
                            </span>
                        </div>
                        <span class="iph-wl-bal-v iph-wl-bal-btc">₿{{ formatBtcCompact(stats.totalInvestedBtc) }}</span>
                        <span class="iph-wl-bal-v iph-wl-bal-usd">{{ currencySymbol }}{{ formatCurrency(stats.totalInvested) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- ══ STAT CARDS ROW — exact clone of d-stat-row / d-stat from Dashboard ══ -->
        <div class="iph-stat-row">

            <!-- Total Invested — Purple  (mirrors d-stat --d:40ms --ac:167,93,237) -->
            <div class="iph-stat anim-up" style="--d:40ms;--ac:167,93,237">
                <div class="iph-glow" :style="`background:radial-gradient(ellipse at 100% 100%,rgba(${themeColors.primaryRgb},0.2),transparent 70%)`"></div>
                <div class="iph-top">
                    <div class="iph-ic" :style="`background:rgba(${themeColors.primaryRgb},0.15);box-shadow:0 0 12px rgba(${themeColors.primaryRgb},0.2);`">
                        <svg width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" :style="`color:${themeColors.primary}`">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                    </div>
                    <span class="iph-lbl">{{ t('totalInvested') }}</span>
                </div>
                <p class="iph-val iph-btc-top">₿{{ formatBtcCompact(stats.totalInvestedBtc) }}</p>
                <div class="iph-usd-line">{{ currencySymbol }}{{ formatCurrency(stats.totalInvested) }}</div>
                <div class="iph-sub" :style="`color:${themeColors.primary}`">+{{ stats.investedGrowth }}% {{ t('thisMonth') }}</div>
                <div class="iph-bar-track"><div class="iph-bar shimmer-bar" :style="`width:${Math.min(stats.investedGrowth * 3 + 15, 82)}%;background:linear-gradient(90deg,#7c3aed,#a75ded,#c084fc)`"></div></div>
            </div>

            <!-- Total Staked — Cyan  (mirrors d-stat --d:80ms --ac:34,211,238) -->
            <div class="iph-stat anim-up" style="--d:80ms;--ac:34,211,238">
                <div class="iph-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(34,211,238,0.15),transparent 70%)"></div>
                <div class="iph-top">
                    <div class="iph-ic" style="background:rgba(34,211,238,0.12);box-shadow:0 0 12px rgba(34,211,238,0.18);">
                        <svg width="15" height="15" fill="none" stroke="#22d3ee" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10"/>
                        </svg>
                    </div>
                    <span class="iph-lbl">{{ t('totalStaked') }}</span>
                </div>
                <p class="iph-val iph-btc-cyan">₿{{ formatBtcCompact(stats.totalStakedBtc ?? 0) }}</p>
                <div class="iph-usd-line">{{ currencySymbol }}{{ formatCurrency(stats.totalStaked) }}</div>
                <div class="iph-sub" style="color:#22d3ee">+{{ stats.stakedGrowth }}% {{ t('thisMonth') }}</div>
                <div class="iph-bar-track"><div class="iph-bar shimmer-bar" style="width:58%;background:linear-gradient(90deg,#0891b2,#22d3ee,#67e8f9)"></div></div>
            </div>

            <!-- Total Returns — Green  (mirrors d-stat --d:120ms --ac:16,185,129) -->
            <div class="iph-stat anim-up" style="--d:120ms;--ac:16,185,129">
                <div class="iph-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(16,185,129,0.15),transparent 70%)"></div>
                <div class="iph-top">
                    <div class="iph-ic" style="background:rgba(16,185,129,0.12);box-shadow:0 0 12px rgba(16,185,129,0.18);">
                        <svg width="15" height="15" fill="none" stroke="#10b981" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                        </svg>
                    </div>
                    <span class="iph-lbl">{{ t('totalReturns') }}</span>
                </div>
                <p class="iph-val">{{ currencySymbol }}{{ formatCurrency(stats.totalReturns) }}</p>
                <div class="iph-sub" style="color:#10b981">+{{ stats.returnsGrowth }}% {{ t('thisMonth') }}</div>
                <div class="iph-bar-track"><div class="iph-bar shimmer-bar" style="width:45%;background:linear-gradient(90deg,#059669,#10b981,#34d399)"></div></div>
            </div>

            <!-- Active Investors — Pink  (mirrors d-stat --d:160ms --ac:232,121,249) -->
            <div class="iph-stat anim-up" style="--d:160ms;--ac:232,121,249">
                <div class="iph-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(232,121,249,0.15),transparent 70%)"></div>
                <div class="iph-top">
                    <div class="iph-ic" style="background:rgba(232,121,249,0.12);box-shadow:0 0 12px rgba(232,121,249,0.18);">
                        <svg width="15" height="15" fill="none" stroke="#e879f9" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                        </svg>
                    </div>
                    <span class="iph-lbl">{{ t('activeUsers') }}</span>
                </div>
                <p class="iph-val">{{ stats.activeUsers.toLocaleString() }}</p>
                <div class="iph-sub" style="color:#e879f9">+{{ stats.newUsers }} {{ t('new') }} {{ t('thisWeek') }}</div>
                <div class="iph-bar-track"><div class="iph-bar shimmer-bar" style="width:33%;background:linear-gradient(90deg,#a21caf,#e879f9,#f0abfc)"></div></div>
            </div>

            <!-- Rio Wallet — Orange (BTC) -->
            <div class="iph-stat anim-up" style="--d:200ms;--ac:255,111,0">
                <div class="iph-glow" style="background:radial-gradient(ellipse at 100% 100%,rgba(255,111,0,0.18),transparent 70%)"></div>
                <div class="iph-top">
                    <div class="iph-ic" style="background:rgba(255,111,0,0.12);box-shadow:0 0 12px rgba(255,111,0,0.18);">
                        <svg width="15" height="15" fill="none" stroke="#FF6F00" stroke-width="2" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="9"/>
                            <path d="M12 6v6l4 2"/>
                        </svg>
                    </div>
                    <span class="iph-lbl">BTC Rewards ₿</span>
                </div>
                <p class="iph-val" style="color:#FF6F00">₿{{ formatBtc(stats.rioBalance) }}</p>
                <div class="iph-sub" style="color:#FF6F00">{{ t('investmentProfits') || 'Investment Profits' }}</div>
                <div class="iph-bar-track"><div class="iph-bar shimmer-bar" style="width:40%;background:linear-gradient(90deg,#ff6f00,#ffb74d,#ffe0b2)"></div></div>
            </div>

        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'

const props = defineProps({
    stats: {
        type: Object,
        required: true,
        default: () => ({
            totalInvested: 0, totalInvestedBtc: 0, totalStaked: 0, totalStakedBtc: 0, totalReturns: 0, activeUsers: 0,
            totalPlans: 0, activePlans: 0, bestApy: 0,
            investedGrowth: 0, stakedGrowth: 0, returnsGrowth: 0, newUsers: 0,
            rioBalance: 0
        })
    }
})

const page = usePage()
const { t } = useTranslation()
const primaryColor   = computed(() => page.props.primaryColor || '#a75ded')
const currencySymbol = computed(() => page.props.currencySymbol || '$')

const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return r ? { r: parseInt(r[1],16), g: parseInt(r[2],16), b: parseInt(r[3],16) } : { r:167,g:93,b:237 }
}
const rgbToHex = (r,g,b) => '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)
const adjustColor = (hex, amt) => {
    const { r,g,b } = hexToRgb(hex)
    return rgbToHex(Math.min(255,Math.max(0,r+amt)), Math.min(255,Math.max(0,g+amt)), Math.min(255,Math.max(0,b+amt)))
}
const themeColors = computed(() => {
    const { r,g,b } = hexToRgb(primaryColor.value)
    return {
        primary:      primaryColor.value,
        primaryDark:  adjustColor(primaryColor.value, -30),
        primaryLight: adjustColor(primaryColor.value, 40),
        primaryRgb:   `${r},${g},${b}`
    }
})

const formatCurrency = (v) => {
    const n = parseFloat(v) || 0
    if (n >= 1e6) return (n/1e6).toFixed(1) + 'M'
    if (n >= 1e3) return (n/1e3).toFixed(1) + 'K'
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
}

const formatBtc = (v) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 12, maximumFractionDigits: 12 }).format(parseFloat(v) || 0)
}

const formatBtcCompact = (v) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 12, maximumFractionDigits: 12 }).format(parseFloat(v) || 0)
}
</script>

<style scoped>
/* ════════ ENTRANCE ANIMATION — same as dashboard ════════ */
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:none } }

/* ════════ ROOT — gap matches d-inner gap at every breakpoint ════════ */
.iph-root { display:flex; flex-direction:column; gap:14px; }
@media(min-width:640px)  { .iph-root { gap:16px; } }
@media(min-width:1024px) { .iph-root { gap:18px; } }

/* ════════ WELCOME STRIP — same shape as d-welcome ════════ */
.iph-welcome {
    position: relative; overflow: hidden;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap;
    padding: 12px 16px; border-radius: 12px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.16);
}

/* Decorative orbs inside welcome strip */
.iph-orb { position:absolute; border-radius:50%; pointer-events:none; filter:blur(45px); }
.iph-orb--1 { width:180px; height:180px; top:-80px; right:-30px; background:rgba(167,93,237,0.2); }
.iph-orb--2 { width:120px; height:120px; bottom:-60px; left:-20px; background:rgba(34,211,238,0.14); }

/* Top shimmer line */
.iph-hero-line {
    position:absolute; top:0; left:10%; right:10%; height:1px; border-radius:999px;
}

/* Welcome content above orbs */
.iph-wl-body {
    position: relative; z-index: 1;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap; width: 100%;
}

/* Left side — avatar + text */
.iph-wl-left { display:flex; align-items:center; gap:10px; flex-shrink:0; }
.iph-wl-av {
    width:38px; height:38px; border-radius:50%; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
}
.iph-wl-kicker { font-size:9px; font-weight:600; text-transform:uppercase; letter-spacing:0.1em; }
.iph-wl-title  { font-size:14px; font-weight:800; color:#fff; letter-spacing:-0.02em; }

/* Chips row — same as dw-chips */
.iph-wl-chips { display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.iph-wl-chip {
    display:flex; flex-direction:column; align-items:center; gap:1px;
    padding:6px 12px; border-radius:9px;
    background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    min-width:56px;
}
.iph-wl-chip-l { font-size:8px; font-weight:700; color:#4b5563; text-transform:uppercase; letter-spacing:0.12em; }
.iph-wl-chip-v { font-size:13px; font-weight:900; color:#fff; }

/* Balance chip — same as dw-bal */
.iph-wl-bal {
    display:flex; flex-direction:column; gap:2px;
    padding:6px 12px; border-radius:9px; border:1px solid;
}
.iph-wl-bal-top { display:flex; align-items:center; gap:6px; }
.iph-wl-live {
    display:inline-flex; align-items:center; gap:3px;
    font-size:7px; font-weight:800; letter-spacing:0.1em;
    padding:1px 5px; border-radius:999px;
}
.iph-wl-dot {
    width:4px; height:4px; border-radius:50%;
    animation: dotPulse 1.6s ease-in-out infinite;
}
@keyframes dotPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
.iph-wl-bal-v { font-size:14px; font-weight:900; color:#fff; letter-spacing:-0.02em; }
.iph-wl-bal-btc { color:#FF6F00; line-height:1.1; }
.iph-wl-bal-usd { font-size:12px; color:rgba(255,255,255,0.82); font-weight:700; }

/* ════════════════════════════════════════════════════════════════
   STAT CARDS — pixel-perfect copy of Dashboard d-stat / ds-* CSS
   ════════════════════════════════════════════════════════════════ */
.iph-stat-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 10px;
}
@media(min-width:768px) { .iph-stat-row { gap: 12px; } }

/* d-stat */
.iph-stat {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 13px;
    padding: 12px 14px;
    position: relative; overflow: hidden;
    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor: default;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 116px; /* ensure uniform card height */
}
.iph-stat:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 32px rgba(0,0,0,0.4), 0 0 20px rgba(var(--ac),0.1);
}

/* ds-glow */
.iph-glow { position:absolute; inset:0; pointer-events:none; }

/* ds-top */
.iph-top { display:flex; align-items:center; gap:8px; margin-bottom:10px; position:relative; z-index:1; }

/* ds-icon */
.iph-ic {
    width:30px; height:30px; border-radius:9px;
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    transition: transform 0.25s;
}
.iph-stat:hover .iph-ic { transform: scale(1.1); }

/* ds-lbl */
.iph-lbl { font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.12em; color:#374151; }

/* ds-val */
.iph-val {
    font-size: 1.05rem; font-weight:900; color:#fff;
    letter-spacing: -0.02em; line-height:1.1;
    position:relative; z-index:1; margin-bottom:3px;
}
@media(min-width:768px) { .iph-val { font-size:1.15rem; } }
@media(min-width:1024px) { .iph-val { font-size:1.2rem; } }

/* Keep modifier same size to ensure uniformity across cards */
.iph-val--small { font-size: inherit; }
.iph-btc-top  { color:#FF6F00; margin-bottom:2px; }
.iph-btc-cyan { color:#22d3ee; margin-bottom:2px; }
.iph-usd-line {
    font-size:0.86rem;
    font-weight:700;
    color:rgba(255,255,255,0.82);
    letter-spacing:-0.01em;
    margin-bottom:6px;
}
@media(min-width:768px) { .iph-usd-line { font-size:0.92rem; } }

/* ds-sub */
.iph-sub { font-size:10px; font-weight:700; position:relative; z-index:1; margin-bottom:10px; }

/* ds-bar-track / ds-bar */
.iph-bar-track { height:2px; background:rgba(255,255,255,0.06); border-radius:999px; overflow:hidden; position:relative; z-index:1; }
.iph-bar       { height:100%; border-radius:999px; }

/* ════════ SHIMMER — same as dashboard ════════ */
.shimmer-bar { position:relative; overflow:hidden; }
.shimmer-bar::after {
    content:''; position:absolute; top:0; left:-150%; width:80%; height:100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: shimmerRun 2.6s ease-in-out infinite;
}
@keyframes shimmerRun { 0%{left:-150%} 100%{left:200%} }

/* ════════ RESPONSIVE ════════ */
@media(max-width:480px) {
    .iph-wl-chip:not(:first-child):not(:last-child) { display:none; }
}

/* ════════ REDUCED MOTION ════════ */
@media(prefers-reduced-motion:reduce) {
    .anim-up            { animation:none !important; opacity:1; transform:none; }
    .shimmer-bar::after { animation:none !important; }
    .iph-wl-dot         { animation:none !important; }
    .iph-stat:hover     { transform:none; }
}
</style>
